import useFlags from 'launchdarkly-react-client-sdk/lib/useFlags';
import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BACKOFFICE_TITLE } from 'basics/constants/common.constants';
import LargeContainer from 'components/Layout/LargeContainer';
import { useCurrentUserState } from 'contexts/user';
import { CountrySelector as EventsCountrySelector } from 'modules/events/components/CountrySelector/CountrySelector';
import CreateEvent from 'modules/events/components/CreateEvent';
import { EventDatePicker } from 'modules/events/components/EventDatePicker/EventDatePicker';
import EventListPagination from 'modules/events/components/EventListPagination/EventListPagination';
import EventsList from 'modules/events/components/EventsList';
import { EventStatusSelector } from 'modules/events/components/EventStatusSelector/EventStatusSelector';
import { EventTypeSelector } from 'modules/events/components/EventTypeSelector/EventTypeSelector';
import ManageJourneys from 'modules/events/components/ManageJourneys';
import ManageSpeakers from 'modules/events/components/ManageSpeakers/ManageSpeakers';
import useEvents from 'modules/events/routes/Events/Events.hook';
import usePastEvents from 'modules/events/routes/Events/PastEvents.hook';
import { ActionButton, ButtonVariant } from 'yoda-ui/components/BiogenLincDS/Buttons';
import Box from 'yoda-ui/components/Box';
import { Icons } from 'yoda-ui/components/Icons/Icon';
import Loader from 'yoda-ui/components/Loader';
import Paper from 'yoda-ui/components/Paper';
import { YodaBorderRadius, YodaColors, YodaFontSize, YodaSpacing } from 'yoda-ui/yodaTheme';

const Events: FC = () => {
  const { t } = useTranslation();
  const eventsListRef = useRef<HTMLDivElement>(null);
  const pastEventsListRef = useRef<HTMLDivElement>(null);
  const {
    upcomingEvents,
    loadingUpcoming,
    onManageSpeakersModalOpen,
    onManageSpeakersModalClose,
    showManageSpeakersModal,
    onManageJourneysModalOpen,
    onManageJourneysModalClose,
    showManageJourneysModal,
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
  } = useEvents(false);
  const { user, userLoading } = useCurrentUserState();
  const {
    pastEvents,
    loadingPast,
    page: pastPage,
    setPage: setPastPage,
    pageSize: pastPageSize,
    setPageSize: setPastPageSize,
    totalCount: pastTotalCount,
  } = usePastEvents();
  const flags = useFlags();

  const shouldScrollUpcoming = useRef(false);
  const shouldScrollPast = useRef(false);

  useEffect(() => {
    if (!loadingUpcoming && shouldScrollUpcoming.current) {
      shouldScrollUpcoming.current = false;
      eventsListRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [loadingUpcoming, upcomingEvents]);

  useEffect(() => {
    if (!loadingPast && shouldScrollPast.current) {
      shouldScrollPast.current = false;
      pastEventsListRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [loadingPast, pastEvents]);

  return (
    <LargeContainer
      flexDirection="column"
      marginBottom={ YodaSpacing.xLarge }
      paddingRight={ YodaSpacing.xLarge }
      paddingLeft={ YodaSpacing.xLarge }
    >
      <Box maxWidth="70%">
        <Box fontSize={ YodaFontSize.xxxxLarge } color={ YodaColors.gray5 }>
          { t('titles_home', { appName: BACKOFFICE_TITLE, userName: user?.name || '' }) }
        </Box>
        <Box
          marginTop={ YodaSpacing.small }
          marginBottom={ YodaSpacing.medium }
          fontSize={ YodaFontSize.xLarge }
          color={ YodaColors.gray4 }
        >
          { t('home_description') }
        </Box>

        <Box display="flex" gap={ YodaSpacing.medium }>
          {
            flags.eventsBiogenlinc4427ManageSpeakers ? (
              <>
                <ActionButton
                  icon={ Icons.campaign }
                  label={ t('event_manage_speakers_title') }
                  variant={ ButtonVariant.primary }
                  onClick={ onManageSpeakersModalOpen }
                />
                <ManageSpeakers isOpen={ showManageSpeakersModal } onClose={ onManageSpeakersModalClose } />
              </>
            ) : null
          }

          {
            flags.eventsCweb2084EnableAddNewJourneyButton ? (
              <>
                <ActionButton
                  icon={ Icons.settings }
                  label={ t('manage_journeys_title') }
                  variant={ ButtonVariant.primary }
                  onClick={ onManageJourneysModalOpen }
                />
                <ManageJourneys isOpen={ showManageJourneysModal } onClose={ onManageJourneysModalClose } />
              </>
            ) : null
          }
        </Box>

      </Box>

      <Box ref={ eventsListRef } sx={ { scrollMarginTop: '120px' } } marginY={ YodaSpacing.xxLarge } width="100%">
        <Box fontSize={ YodaFontSize.xLargePlus } marginBottom={ YodaSpacing.medium }>
          { t('events_filter_events_title') }
        </Box>

        <Paper borderRadius={ YodaBorderRadius.small } margin={ YodaSpacing.none } sx={ { padding: YodaSpacing.large } }>
          <Box display="flex" width="100%" alignItems="center">
            <EventDatePicker />
            <EventStatusSelector />
            <EventTypeSelector></EventTypeSelector>
            { !userLoading && user ? <EventsCountrySelector user={ user } /> : null }
          </Box>
        </Paper>

        <Box
          display="flex"
          width="100%"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          gap={ YodaSpacing.large }
          marginTop={ YodaSpacing.xLarge }
        >
          <Box fontSize={ YodaFontSize.xLargePlus }>
            { t('events_upcoming_events_title') }
          </Box>
          {
            flags.eventsBiogenlinc5767UsMarket ? (
              <CreateEvent />
            ) : null
          }
        </Box>

      </Box>
      {
        upcomingEvents && upcomingEvents.length > 0
          ? (
            <>
              <EventsList events={ upcomingEvents } />
              <EventListPagination
                pageNumber={ page }
                pageSize={ pageSize }
                totalCount={ totalCount }
                setPageNumber={ setPage }
                setPageSize={ setPageSize }
                onPaginationChange={ () => { shouldScrollUpcoming.current = true; } }
              />
            </>
          )
          : !loadingUpcoming && (<Box display="flex" justifyContent="center">{ t('events_no_events_result_label') }</Box>)
      }
      {
        loadingUpcoming && !upcomingEvents.length && (
          <Box display="flex" flexDirection="column" justifyContent="center" marginBottom="20px">
            <Loader center />
          </Box>
        )
      }
      {
        flags.eventsShowPastEvents3525 && (
          <Box ref={ pastEventsListRef } sx={ { scrollMarginTop: '120px' } } marginY={ YodaSpacing.xxLarge } width="100%">
            <Box display="flex" width="100%" flexDirection="row" justifyContent="space-between">
              <Box fontSize={ YodaFontSize.xLargePlus }>
                { t('past_event_list_title') }
              </Box>
            </Box>
          </Box>
        )
      }
      {
        pastEvents.length > 0 && flags.eventsShowPastEvents3525 && (
          <>
            <EventsList events={ pastEvents } />
            <EventListPagination
              pageNumber={ pastPage }
              pageSize={ pastPageSize }
              totalCount={ pastTotalCount }
              setPageNumber={ setPastPage }
              setPageSize={ setPastPageSize }
              onPaginationChange={ () => { shouldScrollPast.current = true; } }
            />
          </>
        )
      }
      {
        loadingPast && flags.eventsShowPastEvents3525 && (
          <Box display="flex" flexDirection="column" justifyContent="center" marginBottom="20px">
            <Loader center />
          </Box>
        )
      }
      {
        pastTotalCount === 0 && !loadingPast && flags.eventsShowPastEvents3525 && (
          <Box display="flex" justifyContent="center">{ t('events_no_past_events_result_label') }</Box>
        )
      }
    </LargeContainer>
  );
};

export default Events;
